import { EApiStatus, TTL } from 'constants/common';
import { useQuery } from '@tanstack/react-query';
import axios from 'plugins/api/axios';

const { teachers, oneClubGateway } = axios;

export const useGetServerTime = (isLogin: boolean) => {
  return useQuery({
    queryKey: ['serverTime'],
    enabled: isLogin,
    queryFn: async () => {
      const { data, status } = await teachers.getServerTime();
      if (status !== EApiStatus.SUCCESS) {
        return '';
      }
      return data;
    },
  });
};

/**
 * 取得「伺服器時間 - 裝置時間」的偏差值（毫秒）
 *
 * 用法：`const serverNow = Date.now() + offsetMs`
 *
 * 比起先抓一次時間再自行 `+1000` 累加（見舊版 Clock），這個做法在每次使用時重算，
 * 不受瀏覽器對背景分頁的 timer 節流影響，不會愈走愈慢。
 *
 * 不沿用 `useGetServerTime` 的原因：它只回字串，而全域 staleTime 是 5 分鐘，
 * 拿到的可能是 5 分鐘前的快取值，據此算出的 offset 會整整差 5 分鐘。
 * 這裡改成在 queryFn 內量測，並以 RTT 的一半補償單程延遲。
 *
 * 取不到時 `offsetMs` 為 0（退回裝置時間），不 reject —— 拿不到伺服器時間不該用 toast 打斷使用者。
 * 需要「拿到之前先不要顯示」的使用端可以判斷 `isReady`。
 */
export const useServerTimeOffset = (enabled = true) => {
  const { data, isSuccess } = useQuery({
    queryKey: ['serverTimeOffset'],
    enabled,
    queryFn: async () => {
      const requestedAt = Date.now();
      const { data, status } = await teachers.getServerTime();
      const respondedAt = Date.now();

      if (status !== EApiStatus.SUCCESS || !data) return 0;

      const serverMs = new Date(data).getTime();
      if (Number.isNaN(serverMs)) return 0;

      // 假設上下行延遲對稱，伺服器產生時間戳的瞬間 ≈ requestedAt + RTT / 2
      return serverMs - (requestedAt + (respondedAt - requestedAt) / 2);
    },
    // offset 只隨 NTP drift 緩慢變化，不需要頻繁重抓；
    // 10 分鐘讓「離開很久再回來」靠 refetchOnWindowFocus 自動校正，又不會每次切分頁都多打一發。
    staleTime: 10 * TTL.ONE_MINUTE,
    // 使用端會隨 tab 切換 unmount，預設 5 分鐘的 cacheTime 會讓它被 GC 掉。
    cacheTime: TTL.ONE_HOUR,
    retry: 1,
  });

  return { offsetMs: data ?? 0, isReady: isSuccess };
};

export const useGetEarlyLeaveTime = (isLogin: boolean, isEnabled: boolean) => {
  return useQuery({
    queryKey: ['earlyLeaveTime'],
    enabled: isLogin && isEnabled,
    queryFn: async () => {
      const { data, status } = await oneClubGateway.earlyLeaveTime();
      if (status !== EApiStatus.SUCCESS) {
        return 0;
      }
      return data.earlyLeaveTime;
    },
  });
};
